import React, { useState, useEffect } from "react";
import Popup from "components/pop-up/pop-up";
import { useTooltip } from "context/TooltipProvider";
import { useTranslation } from "react-i18next";
import {
  addAssetReferralProhbitionRequest,
  fetchAssetReferralById,
  updateAssetReferralProhibitionRequest,
} from "services/assetRecoveryService";
import {
  Referral,
  Asset,
  AssetReferralActionStatusEnum,
  AssetReferralForwardingStatusEnum,
  ProhibitionRequest, // Add this type
} from "types";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";
import { getMappingDisplayText } from "utils/constant";

interface ProsecutorDecisionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  referralId: string;
  referral: Referral | null;
  onDecsionCreated: (res: any) => void;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;

  // New props for handling multiple prohibitions
  prohibitionId?: string | null; // ID of the specific prohibition to edit
  editData?: ProhibitionRequest | null; // Data for the specific prohibition
  mode: "create" | "edit"; // Mode to determine if we're creating new or editing existing
  onEditComplete?: () => void; // Callback after edit is complete
  assetsNeedingReport: any[];
}

const ProsecutorDecisionPopup: React.FC<ProsecutorDecisionPopupProps> = ({
  isOpen,
  onClose,
  referralId,
  referral,
  onDecsionCreated,
  setActiveTab,
  prohibitionId,
  editData,
  mode = "create",
  onEditComplete,
  assetsNeedingReport,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const [decisionType, setDecisionType] = useState<string>("");
  const [prohibitionRequestSummary, setProhibitionRequestSummary] =
    useState<string>("");
  const [prohibitionRequestDate, setProhibitionRequestDate] =
    useState<string>("");
  const [prohibitionRequestDocument, setProhibitionRequestDocument] = useState<
    File[]
  >([]);
  const [existingDocuments, setExistingDocuments] = useState<any[]>([]); // For displaying existing files
  const [decisionSummary, setDecisionSummary] = useState<string>("");
  const [decisionDate, setDecisionDate] = useState<string>("");
  const [decisionDocument, setDecisionDocument] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);

  const [suspect_asset_mapping, setSuspectAssetMapping] = useState<string>("");
  // Check if we're in edit mode based on props
  const isEditMode = mode === "edit" && editData && prohibitionId;

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && editData) {
        console.log(
          "Initializing edit mode for prohibition:",
          prohibitionId,
          editData,
        );
        setDecisionType("request"); // Always request type for edit mode

        // Populate form with existing prohibition data
        if (editData.prohabition_request_summary) {
          setProhibitionRequestSummary(editData.prohabition_request_summary);
        }
        if (editData.prohabition_request_date) {
          // Format date if needed
          const date = editData.prohabition_request_date.split("T")[0];
          setProhibitionRequestDate(date);
        }

        // Store existing documents if any
        if (editData.prohabition_request_document) {
          setExistingDocuments(editData.prohabition_request_document);
        }

        // Don't set the document files as they're File objects, just store the existing ones for display
      } else {
        // Reset form for new entry
        console.log("Initializing create mode");
        resetForm();
      }
    }
  }, [isOpen, mode, editData, prohibitionId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isEditMode && !decisionType) {
      showTooltip(t("please_select_a_decision_type"), "error");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("referral", referralId);

      // For edit mode, include the prohibition_id
      if (isEditMode && prohibitionId) {
        formData.append("prohibition_id", prohibitionId);
      }

      // Append decision type (only for new entries, not for edits)
      if (!isEditMode) {
        formData.append("decision_type", decisionType);
      }

      // Append fields based on decision type or edit mode
      if (isEditMode || decisionType === "request") {
        // For edit mode or request type, include prohibition request data
        if (prohibitionRequestSummary) {
          formData.append(
            "prohabition_request_summary",
            prohibitionRequestSummary,
          );
        }

        if (prohibitionRequestDate) {
          formData.append("prohabition_request_date", prohibitionRequestDate);
        }

        // Only append new files if any were uploaded
        if (prohibitionRequestDocument.length > 0) {
          prohibitionRequestDocument.forEach((file) => {
            formData.append("prohibition_request_documents", file);
          });
        }

        if (suspect_asset_mapping) {
          formData.append("count_suspect_asset", suspect_asset_mapping);
        }
        formData.append("is_prohabition_requested", "true");

        // Only set these statuses for new entries
        if (!isEditMode) {
          formData.append(
            "status",
            AssetReferralActionStatusEnum.PROHIBITION_REQUESTED,
          );
          formData.append(
            "forward_status",
            AssetReferralForwardingStatusEnum.FORWARDED_TO_COORDINATOR,
          );
        }
      } else if (decisionType === "close") {
        // For close type
        if (decisionSummary) {
          formData.append("decision_summary", decisionSummary);
        }

        if (decisionDate) {
          formData.append("decision_date", decisionDate);
        }

        if (decisionDocument.length > 0) {
          decisionDocument.forEach((file) => {
            formData.append("decision_document", file);
          });
        }
        formData.append(
          "status",
          AssetReferralActionStatusEnum.CLOSED_BY_PROSECUTOR,
        );
        formData.append("is_prohabition_requested", "false");
      }

      let response;

      if (isEditMode) {
        // Call update API for existing prohibition
        // You'll need to create this service function
        response = await updateAssetReferralProhibitionRequest(
          prohibitionId,
          formData,
        );
        showTooltip(t("prohibition_request_updated_successfully"), "success");
      } else {
        // Create new prohibition request
        response = await addAssetReferralProhbitionRequest(formData);
        const successMessage =
          decisionType === "close"
            ? "Case closed successfully!"
            : "Prohibition request submitted successfully!";
        showTooltip(successMessage, "success");
      }

      onClose();
      resetForm();

      // Refresh data
      if (onDecsionCreated) {
        const updatedReferral = await fetchAssetReferralById(referralId);
        onDecsionCreated(updatedReferral);
        setActiveTab?.("Prohibition");
      }

      // Call edit complete callback if provided
      if (isEditMode && onEditComplete) {
        onEditComplete();
      }
    } catch (error) {
      const errorMessage = isEditMode
        ? "Failed to update prohibition request"
        : decisionType === "close"
          ? "Failed to close case"
          : "Failed to submit prohibition request";
      showTooltip(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setDecisionType("");
    setProhibitionRequestSummary("");
    setProhibitionRequestDate("");
    setProhibitionRequestDocument([]);
    setExistingDocuments([]);
    setDecisionSummary("");
    setDecisionDate("");
    setDecisionDocument([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Popup isOpen={isOpen}>
      <div
        className="referral-details-popup-container"
        style={{ maxWidth: "650px" }}
      >
        <div className="pop-up-form">
          <div className="form-header flex justify-between items-center">
            <div>
              <p className="referral-popup-title">
                {isEditMode
                  ? "Edit Prohibition Request"
                  : "Prosecutor Decision"}
              </p>
              <p className="description">
                {isEditMode
                  ? "Update your prohibition request details"
                  : "Make a decision for unlawful assets"}
              </p>
              {isEditMode && prohibitionId && (
                <p className="text-xs text-gray-500 mt-1">
                  Prohibition ID: {prohibitionId}
                </p>
              )}
            </div>

            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={handleClose}
              className="cursor-pointer"
            >
              <path
                d="M16.95 8.46404C17.1322 8.27544 17.233 8.02283 17.2307 7.76064C17.2284 7.49844 17.1232 7.24763 16.9378 7.06222C16.7524 6.87681 16.5016 6.77164 16.2394 6.76936C15.9772 6.76709 15.7246 6.86788 15.536 7.05004L12 10.586L8.464 7.05004C8.37175 6.95453 8.26141 6.87835 8.13941 6.82594C8.0174 6.77353 7.88618 6.74594 7.7534 6.74479C7.62062 6.74363 7.48894 6.76894 7.36605 6.81922C7.24315 6.8695 7.1315 6.94375 7.03761 7.03764C6.94371 7.13154 6.86946 7.24319 6.81918 7.36609C6.7689 7.48898 6.7436 7.62066 6.74475 7.75344C6.7459 7.88622 6.77349 8.01744 6.8259 8.13944C6.87831 8.26145 6.95449 8.37179 7.05 8.46404L10.586 12L7.05 15.536C6.95449 15.6283 6.87831 15.7386 6.8259 15.8606C6.77349 15.9826 6.7459 16.1139 6.74475 16.2466C6.7436 16.3794 6.7689 16.5111 6.81918 16.634C6.86946 16.7569 6.94371 16.8685 7.03761 16.9624C7.1315 17.0563 7.24315 17.1306 7.36605 17.1809C7.48894 17.2311 7.62062 17.2564 7.7534 17.2553C7.88618 17.2541 8.0174 17.2265 8.13941 17.1741C8.26141 17.1217 8.37175 17.0456 8.464 16.95L12 13.414L15.536 16.95C15.6282 17.0456 15.7386 17.1217 15.8606 17.1741C15.9826 17.2265 16.1138 17.2541 16.2466 17.2553C16.3794 17.2564 16.5111 17.2311 16.634 17.1809C16.7569 17.1306 16.8685 17.0563 16.9624 16.9624C17.0563 16.8685 17.1305 16.7569 17.1808 16.634C17.2311 16.5111 17.2564 16.3794 17.2553 16.2466C17.2541 16.1139 17.2265 15.9826 17.1741 15.8606C17.1217 15.7386 17.0455 15.6283 16.95 15.536L13.414 12L16.95 8.46404Z"
                fill="black"
              />
            </svg>
          </div>

          {/* Decision Type Selection - Only show for new entries */}
          {!isEditMode && (
            <div className="flex flex-col">
              <label className="referral-popup-label">Decision Type *</label>
              <select
                className="referral-form-field"
                value={decisionType}
                onChange={(e) => setDecisionType(e.target.value)}
                required
              >
                <option value="">Select Decision Type</option>
                <option value="request">Request</option>
                <option value="close">Close</option>
              </select>
            </div>
          )}

          <div>
            <form onSubmit={handleSubmit}>
              {/* Conditional Form Fields */}
              {!isEditMode && decisionType === "close" && (
                <>
                  <div className="flex flex-col mt-4">
                    <label className="referral-popup-label">
                      Decision Summary *
                    </label>
                    <textarea
                      className="referral-form-field h-24"
                      placeholder="Enter decision summary"
                      value={decisionSummary}
                      onChange={(e) => setDecisionSummary(e.target.value)}
                      required
                    />
                  </div>

                  <div className="flex flex-col mt-4">
                    <label className="referral-popup-label">
                      Decision Date *
                    </label>
                    <input
                      type="date"
                      className="referral-form-field"
                      value={decisionDate}
                      onChange={(e) => setDecisionDate(e.target.value)}
                      required
                    />
                  </div>

                  <div className="mt-4">
                    <MultipleFileUploadComponent
                      uploadedFiles={decisionDocument}
                      onFilesChange={setDecisionDocument}
                    />
                  </div>
                </>
              )}

              {(isEditMode || (!isEditMode && decisionType === "request")) && (
                <>
                  <div className="flex flex-col mb-4">
                    <label className="referral-form-label">
                      Select Suspect-Asset Mapping
                    </label>
                    <select
                      className="referral-form-field"
                      name="suspect_asset_mapping"
                      value={suspect_asset_mapping}
                      onChange={(e) => setSuspectAssetMapping(e.target.value)}
                    >
                      <option value="">Select Mapping</option>
                      {assetsNeedingReport.map((mapping) => (
                        <option key={mapping.id} value={mapping.id}>
                          {getMappingDisplayText(mapping)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col">
                    <label className="referral-popup-label">
                      Prohibition Request Summary *
                    </label>
                    <textarea
                      className="referral-form-field h-24"
                      placeholder="Enter prohibition request summary"
                      value={prohibitionRequestSummary}
                      onChange={(e) =>
                        setProhibitionRequestSummary(e.target.value)
                      }
                      required
                    />
                  </div>

                  <div className="flex flex-col mt-4">
                    <label className="referral-popup-label">
                      Prohibition Request Date *
                    </label>
                    <input
                      type="date"
                      className="referral-form-field"
                      value={prohibitionRequestDate}
                      onChange={(e) =>
                        setProhibitionRequestDate(e.target.value)
                      }
                      required
                    />
                  </div>

                  <div className="mt-4">
                    <MultipleFileUploadComponent
                      onFilesChange={setProhibitionRequestDocument}
                      uploadedFiles={prohibitionRequestDocument}
                    />
                    {isEditMode && existingDocuments.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {existingDocuments.length} existing file(s). Upload new
                        files to add or replace.
                      </p>
                    )}
                  </div>
                </>
              )}

              <div className="mt-6 mb-4">
                <button
                  type="submit"
                  disabled={
                    !!(
                      loading ||
                      (!isEditMode && !decisionType) ||
                      (isEditMode && !prohibitionRequestSummary)
                    )
                  }
                  className={`referral-form-button text-white w-full ${
                    loading || (!isEditMode && !decisionType)
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {loading
                    ? "Submitting..."
                    : isEditMode
                      ? "Update Prohibition Request"
                      : decisionType === "close"
                        ? "Close Case"
                        : "Submit Prohibition Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default ProsecutorDecisionPopup;
